import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useCRM } from '../context/CRMContext';
import BaseModal from '../components/modals/BaseModal';

const EmailTemplates = () => {
  const { 
    emailTemplates, systemEmailSettings, addEmailTemplate, 
    updateEmailTemplate, deleteEmailTemplate, toggleSystemEmail, currentUser,
    toggleHelp // Hentet inn toggleHelp her
  } = useCRM();
  
  const [activeTab, setActiveTab] = useState('custom');
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({ name: '', subject: '', body: '' });

  const handleOpenModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({ name: template.name, subject: template.subject, body: template.body });
    } else {
      setEditingTemplate(null);
      setFormData({ name: '', subject: '', body: '' });
    }
    setIsModalOpen(true);
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (editingTemplate) {
      updateEmailTemplate(editingTemplate.id, { ...formData, owner: editingTemplate.owner });
    } else {
      addEmailTemplate({ ...formData, owner: currentUser?.name || 'Ukjent' });
    }
    setIsModalOpen(false);
  };

  const handleDelete = (id) => {
    if (window.confirm('Er du sikker på at du vil slette denne malen?')) {
      deleteEmailTemplate(id);
    }
  };

  const renderCustomTemplates = () => {
    const filteredTemplates = emailTemplates.filter(t => 
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.subject.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <div className="relative w-64">
            <input 
              type="text" 
              placeholder="Søk i maler..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            />
            <SafeIcon icon="🔍" className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          </div>
          <button 
            onClick={() => handleOpenModal()}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            <SafeIcon icon="➕" className="w-4 h-4 mr-2" /> Ny Mal
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200">
              <tr>
                <th className="px-6 py-3">NAVN</th>
                <th className="px-6 py-3">EMNE</th>
                <th className="px-6 py-3">EIER</th>
                <th className="px-6 py-3">OPPDATERT</th>
                <th className="px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredTemplates.map(template => (
                <tr key={template.id} className="hover:bg-gray-50 group">
                  <td className="px-6 py-4 font-medium text-blue-600 cursor-pointer" onClick={() => handleOpenModal(template)}>
                    {template.name}
                  </td>
                  <td className="px-6 py-4 text-gray-700">{template.subject}</td>
                  <td className="px-6 py-4 text-gray-600">{template.owner}</td>
                  <td className="px-6 py-4 text-gray-500">{template.updated_at || 'I dag'}</td>
                  <td className="px-4 py-4 text-right">
                    <div className="flex items-center justify-end space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => handleOpenModal(template)} className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Rediger">
                        <SafeIcon icon="✏️" className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(template.id)} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Slett">
                        <SafeIcon icon="🗑️" className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredTemplates.length === 0 && (
                <tr><td colSpan="5" className="px-6 py-12 text-center text-gray-500">Ingen maler funnet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderSystemSettings = (category) => {
    const items = systemEmailSettings.filter(s => s.category === category);
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm divide-y divide-gray-100">
        {items.map(item => (
          <div key={item.id} className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
            <div className="flex items-center space-x-4 flex-1">
              <button 
                onClick={() => toggleSystemEmail(item.id)}
                className={`flex-shrink-0 transition-colors duration-200 focus:outline-none`}
              >
                {item.enabled ? <SafeIcon icon="✅" className="w-10 h-10 text-blue-600" /> : <SafeIcon icon="⭕" className="w-10 h-10 text-gray-300" />}
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold text-gray-900">{item.name}</h4>
                  <button className="text-gray-400 hover:text-blue-600">
                    <SafeIcon icon="✏️" className="w-3 h-3" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-0.5">{item.description}</p>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="p-8 text-center text-gray-500">Ingen innstillinger i denne kategorien.</div>}
      </div>
    );
  };

  const tabs = [
    { id: 'custom', label: 'Egendefinerte maler' },
    { id: 'faktura', label: 'Faktura' },
    { id: 'dokumenter', label: 'Dokumenter' },
    { id: 'kunder', label: 'Kunder' }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Sett opp E-postmaler</h1>
        {/* Oppdatert onClick for å bruke den synkroniserte hjelpe-skuffen */}
        <button onClick={toggleHelp} className="text-sm text-blue-600 font-bold flex items-center hover:underline bg-blue-50 px-3 py-1.5 rounded-lg">
          Hvordan komme i gang? <SafeIcon icon="💡" className="ml-2 w-4 h-4" />
        </button>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {activeTab === 'custom' ? renderCustomTemplates() : renderSystemSettings(activeTab)}
      </motion.div>

      <BaseModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingTemplate ? "Rediger E-postmal" : "Ny E-postmal"}>
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mal navn</label>
            <input 
              required 
              value={formData.name} 
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="F.eks. Velkomstmail"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Emne</label>
            <input 
              required 
              value={formData.subject} 
              onChange={(e) => setFormData({...formData, subject: e.target.value})}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="E-postens emnefelt"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Innhold</label>
            <textarea 
              required 
              rows={8}
              value={formData.body} 
              onChange={(e) => setFormData({...formData, body: e.target.value})}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 font-mono"
              placeholder="Hei [Receiver.FirstName]..."
            />
            <p className="mt-1 text-xs text-gray-500">Tilgjengelige variabler: [Receiver.FirstName], [Receiver.Company], [Sender.Name], [Sender.Company]</p>
          </div>
          <div className="flex justify-end pt-4 border-t border-gray-100">
            <button type="button" onClick={() => setIsModalOpen(false)} className="mr-3 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Avbryt</button>
            <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">{editingTemplate ? 'Lagre endringer' : 'Opprett mal'}</button>
          </div>
        </form>
      </BaseModal>

    </div>
  );
};

export default EmailTemplates;